import CloseIcon from "@mui/icons-material/Close";
import RightIcon from "@mui/icons-material/East";
import LeftIcon from "@mui/icons-material/West";
import { useMemo, useRef, useState } from "react";

import {
  EdrObservationsMapLayer,
  EdrObservationsMapLayerDataLocationsInner as EdrObservationsLayerLocation,
  LineGraphEdrLine,
} from "@/lib/api";
import { WidgetContentContext } from "@/features/dashboard/components/widget-container-card";
import DataTable from "@/features/shared/widgets/datatable/datatable";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import {
  OverlayControl,
  OverlayControlHeader,
  OverlayControlDisplayButton,
  OverlayControlDisplayToggle,
  OverlayControlContent,
} from "@/features/shared/widgets/map/controls/overlay-control.styles";
import Graph from "@/features/shared/widgets/graphs/graph";

interface LocationOverlayControlProps {
  location: EdrObservationsLayerLocation;
  layer: EdrObservationsMapLayer;
  handleClose: (value: boolean) => void;
  currentTime: TimeRangeDimensionValue;
}

const LocationOverlayControl = ({
  location,
  layer,
  handleClose,
  currentTime,
}: LocationOverlayControlProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(false);
  const [display, setDisplay] = useState<"graph" | "table">("graph");

  const sortedParams = useMemo(() => {
    return layer.parameter_sets.reduce<string[]>((acc, curr) => {
      return [...acc, ...curr.parameters];
    }, []);
  }, [layer]);

  const data = useMemo(() => {
    if (!layer) return [];

    const dataByUnit =
      location.data?.reduce<{ [unit: string]: typeof location.data }>((acc, curr) => {
        const unit = curr.parameter.unit.type;

        const hasAnyValue = curr.datapoints.some((d) => d.value);
        if (!hasAnyValue) return acc;

        return {
          ...acc,
          [unit]: [...(acc[unit] ?? []), curr],
        };
      }, {}) ?? {};

    return dataByUnit;
  }, [layer, location.data]);

  if (!location.data) return null;

  return (
    <OverlayControl expanded={expanded}>
      <OverlayControlHeader transparent={false}>
        {expanded ? (
          <RightIcon onClick={() => setExpanded(false)} />
        ) : (
          <LeftIcon onClick={() => setExpanded(true)} />
        )}
        <strong>{location.name}</strong>
        <CloseIcon onClick={() => handleClose(true)} />
      </OverlayControlHeader>

      <OverlayControlDisplayToggle>
        <OverlayControlDisplayButton
          active={display === "graph"}
          onClick={() => setDisplay("graph")}
        >
          Graph
        </OverlayControlDisplayButton>
        <OverlayControlDisplayButton
          active={display === "table"}
          onClick={() => setDisplay("table")}
        >
          Table
        </OverlayControlDisplayButton>
      </OverlayControlDisplayToggle>

      <OverlayControlContent ref={contentRef}>
        <WidgetContentContext.Provider
          value={{
            ref: contentRef,
          }}
        >
          {display === "graph" &&
            Object.entries(data)
              .sort(([, aData], [, bData]) => {
                const aIndex = sortedParams.indexOf(aData[0].parameter.id);
                const bIndex = sortedParams.indexOf(bData[0].parameter.id);
                return aIndex < bIndex ? -1 : 1;
              })
              .map(([key, data]) => {
                return (
                  <Graph
                    key={key}
                    data={{
                      lines: data.map((line) => ({
                        id: line.parameter.id,
                        data: line,
                      })),
                    }}
                    configuration={{
                      type: "line_graph",
                      inputs: {
                        location: false,
                        time_range: false,
                      },
                      lines: data.map(
                        (line) =>
                          ({
                            id: line.parameter.id,
                            title: line.parameter.description,
                            edr_parameter: line.parameter.id,
                          }) as LineGraphEdrLine,
                      ),
                    }}
                    options={{
                      chart: {
                        height: 400,
                      },
                    }}
                    currentTime={currentTime?.timestamp}
                  />
                );
              })}
          {display === "table" && <DataTable data={location.data} />}
        </WidgetContentContext.Provider>
      </OverlayControlContent>
    </OverlayControl>
  );
};

export default LocationOverlayControl;
